import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Megaphone, ClipboardText, ArrowLeft } from '@phosphor-icons/react';
import { motion } from 'framer-motion';

import { EmptyState } from '@/components/ui';
import { PollCard } from '@/components/features/PollCard/PollCard';
import { PollCardSkeleton } from '@/components/features/PollCardSkeleton/PollCardSkeleton';
import { FormCard } from '@/components/features/FormCard/FormCard';
import { usePolls, useForms } from '@/api';
import { ROUTES } from '@/constants';

import styles from './MyPolls.module.css';

type TabType = 'polls' | 'forms';

export function MyPolls() {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<TabType>('polls');

    useEffect(() => {
        document.title = 'Mis Creaciones — Ratio';
    }, []);

    const { data: pollsData, isLoading: pollsLoading } = usePolls({ mine: true, ordering: '-created' });
    const { data: formsData, isLoading: formsLoading } = useForms({ mine: true, ordering: '-created' } as Parameters<typeof useForms>[0]);

    const polls = pollsData?.pages.flatMap((page) => page.results) ?? [];
    const forms = formsData?.pages.flatMap((page) => page.results) ?? [];

    const isLoading = activeTab === 'polls' ? pollsLoading : formsLoading;

    return (
        <div className={styles.page}>
            <button
                className={styles.backBtn}
                onClick={() => navigate(-1)}
                aria-label="Volver"
            >
                <ArrowLeft size={18} />
                Volver
            </button>

            <div className={styles.header}>
                <h1 className={styles.title}>Mis creaciones</h1>
                <button
                    className={styles.createBtn}
                    onClick={() => navigate(activeTab === 'polls' ? ROUTES.create : ROUTES.createForm)}
                >
                    <Plus size={16} weight="bold" />
                    Crear
                </button>
            </div>

            {/* Tabs */}
            <div className={styles.tabs}>
                <button
                    type="button"
                    className={`${styles.tab} ${activeTab === 'polls' ? styles.tabActive : ''}`}
                    onClick={() => setActiveTab('polls')}
                >
                    <Megaphone size={15} />
                    Mis Encuestas
                </button>
                <button
                    type="button"
                    className={`${styles.tab} ${activeTab === 'forms' ? styles.tabActive : ''}`}
                    onClick={() => setActiveTab('forms')}
                >
                    <ClipboardText size={15} />
                    Mis Formularios
                </button>
            </div>

            {/* Polls tab */}
            {activeTab === 'polls' && (
                <>
                    {isLoading ? (
                        <div className={styles.list}>
                            {[1, 2, 3].map((i) => (
                                <PollCardSkeleton key={i} />
                            ))}
                        </div>
                    ) : polls.length === 0 ? (
                        <EmptyState
                            icon={<Megaphone weight="duotone" />}
                            title="No has creado encuestas aun"
                            description="Crea tu primera encuesta y comparte con la comunidad"
                            actionLabel="Crear encuesta"
                            onAction={() => navigate(ROUTES.create)}
                        />
                    ) : (
                        <div className={styles.list}>
                            {polls.map((poll) => (
                                <motion.div
                                    key={poll.id}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.2 }}
                                >
                                    <PollCard poll={poll} />
                                </motion.div>
                            ))}
                        </div>
                    )}
                </>
            )}

            {/* Forms tab */}
            {activeTab === 'forms' && (
                <>
                    {isLoading ? (
                        <div className={styles.list}>
                            {[1, 2, 3].map((i) => (
                                <PollCardSkeleton key={i} />
                            ))}
                        </div>
                    ) : forms.length === 0 ? (
                        <EmptyState
                            icon={<ClipboardText weight="duotone" />}
                            title="No has creado formularios aun"
                            description="Crea tu primer formulario y recibe respuestas"
                            actionLabel="Crear formulario"
                            onAction={() => navigate(ROUTES.createForm)}
                        />
                    ) : (
                        <div className={styles.list}>
                            {forms.map((form) => (
                                <motion.div
                                    key={form.id}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.2 }}
                                >
                                    <FormCard form={form} />
                                </motion.div>
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
